import { useCallback, useMemo, useState } from 'react'
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table'
import { DataTable, DataTableProvider } from 'components/DataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import { TwoLineDateCell } from 'components/Tables/CommonTableCells/TwoLineDateCell'
import { PiFileText } from 'react-icons/pi'
import { Plus } from 'lucide-react'
import FbButton from 'components/FbUI/FbButton'
import { useNavigate } from 'react-router-dom'
import {
  ActionsDropdown,
  createDeleteAction,
  createEditAction,
} from 'components/Buttons/ActionsDropdown'
import dialogConfirm from 'components/dialogConfirm'
import { useListBrands, useDeleteBrand } from 'services/products'
import type { Brand } from 'types/brands'
import { toast } from 'react-toastify'
import { BrandModal } from './BrandModal'
import * as S from '../CommonTable.styles'
import { TableSearch } from 'components/Filters/TableSearch'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterIdentifier } from 'models/saved_view'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'

const columnHelper = createColumnHelper<Brand>()

/** Renders a date value as date + time stacked vertically. */
function renderDateCell(value: string | null | undefined) {
  return <TwoLineDateCell value={value} />
}

interface BrandsTableProps {
  tableKey: string
}

/** Inner table component with brand listing, search, create/edit/delete actions. */
function BrandsTableInner({ tableKey }: BrandsTableProps) {
  const BRANDS_IDENTIFIER = `${tableKey}_BRANDS` as FilterIdentifier
  const brandsFilterStore = filterStoreRepo.getStore(BRANDS_IDENTIFIER)
  const filtersAsParams = useFilterParams(brandsFilterStore)
  const linkVariant = useTableLinkVariant()

  const navigate = useNavigate()

  const { data, isLoading, refetch } = useListBrands({
    search: filtersAsParams.search as string | undefined,
    page: 1,
    page_size: 1000,
    sort: 'name',
  })
  const deleteBrand = useDeleteBrand()

  const [showModal, setShowModal] = useState(false)
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null)

  const handleDelete = useCallback(
    async (brand: Brand) => {
      const confirmed = await dialogConfirm(
        `Are you sure you want to delete the brand "${brand.name}"?`,
        'Delete Brand'
      )
      if (!confirmed) return

      try {
        await deleteBrand.mutateAsync({ id: brand.id })
        toast.success('Brand deleted successfully')
        refetch()
      } catch (error) {
        console.error('Error deleting brand:', error)
        toast.error('Failed to delete brand')
      }
    },
    [deleteBrand, refetch]
  )

  const columns = useMemo<ColumnDef<Brand, any>[]>(
    () => [
      columnHelper.accessor('name', {
        header: 'Brand Name',
        meta: {
          isFirstColumn: true,
          isEditable: false,
        },
        cell: (info) => info.getValue() || '—',
        size: 300,
        enableSorting: true,
        enableHiding: false,
      }),
      columnHelper.accessor('image_url', {
        header: 'Brand Image',
        meta: {
          isEditable: false,
          headerDisableOrdering: true,
        },
        cell: (info) => {
          const url = info.getValue()
          return url ? (
            <img
              src={url}
              alt={info.row.original.name}
              className="h-8 w-8 rounded object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100 text-xxs text-gray-400">
              —
            </div>
          )
        },
        size: 100,
        enableSorting: false,
        enableHiding: false,
      }),
      columnHelper.accessor('document_count', {
        header: 'Documents',
        cell: (info) => info.getValue() ?? 0,
        size: 110,
        enableSorting: true,
      }),
      columnHelper.accessor('variant_count', {
        header: 'Product Variants',
        cell: (info) => info.getValue() ?? 0,
        size: 110,
        enableSorting: true,
      }),
      columnHelper.accessor('created', {
        header: 'Created',
        cell: (info) => renderDateCell(info.getValue()),
        size: 160,
        enableSorting: true,
        // renderDateCell stacks date over time — keep two lines.
        meta: { multiLine: true },
      }),
      columnHelper.accessor('modified', {
        header: 'Modified',
        cell: (info) => renderDateCell(info.getValue()),
        size: 160,
        enableSorting: true,
        // renderDateCell stacks date over time — keep two lines.
        meta: { multiLine: true },
      }),
      {
        id: 'actions',
        header:
          linkVariant === 'plain'
            ? () => <div className="w-full text-center">Actions</div>
            : 'Actions',
        size: linkVariant === 'plain' ? 70 : 100,
        enableHiding: false,
        meta: {
          isEditable: false,
          headerDisableOrdering: true,
          isLastColumn: true,
          rightAlign: linkVariant !== 'plain',
          headerGroup: 'Actions',
        },
        cell: ({ row }: CellContext<Brand, unknown>) => {
          const brand = row.original
          return (
            <ActionsDropdown
              actions={[
                createEditAction(() => {
                  setEditingBrand(brand)
                  setShowModal(true)
                }),
                {
                  label: 'View Documents',
                  icon: PiFileText,
                  onClick: () => {
                    const brandFilter = [
                      { value: String(brand.id), label: brand.name },
                    ]
                    // Set store state directly for already-cached stores
                    const store = filterStoreRepo.getStore(
                      'DOCUMENTS_ALL_TABLE'
                    )
                    store.setState({
                      valueFilters: { brand: brandFilter },
                      pendingValueFilters: { brand: brandFilter },
                      criteria: 'all' as const,
                      pendingCriteria: 'all' as const,
                      page: 1,
                      filter_id: undefined,
                    })
                    // Also pass URL params so freshly-created stores pick
                    // up the filter during async persist rehydration
                    const filterState = JSON.stringify({
                      valueFilters: { brand: brandFilter },
                      criteria: 'all',
                    })
                    navigate(
                      `/documents/all?DOCUMENTS_ALL_TABLE=${encodeURIComponent(filterState)}`
                    )
                  },
                },
                createDeleteAction(() => handleDelete(brand)),
              ]}
              containerWidth={170}
              className={linkVariant === 'plain' ? 'justify-center' : undefined}
            />
          )
        },
      },
    ],
    [handleDelete, navigate, brandsFilterStore, linkVariant]
  )

  const tableData = useMemo(() => data?.results ?? [], [data?.results])

  return (
    <>
      <DataTable
        tableKey={tableKey}
        tableHeader={
          <S.SearchContainer>
            <div className="flex w-full items-center gap-3">
              <TableSearch
                className="flex-1"
                filterStore={brandsFilterStore}
                searchPlaceholder="Search brands..."
                tableKey={tableKey}
              />
              <FbButton
                className="!bg-brand-bright !text-white hover:!bg-brand-active"
                onClick={() => {
                  setEditingBrand(null)
                  setShowModal(true)
                }}
              >
                <Plus size={16} />
                Create Brand
              </FbButton>
            </div>
          </S.SearchContainer>
        }
        data={tableData}
        columns={columns}
        loading={isLoading}
        enableRowSelection={false}
        enableColumnResizing={linkVariant === 'plain'}
        rowHeight={linkVariant === 'plain' ? 44 : undefined}
        headerHeight={linkVariant === 'plain' ? 40 : undefined}
        virtualizeRows={false}
        isPaginationEnabled={false}
      />

      <BrandModal
        show={showModal}
        onHide={() => {
          setShowModal(false)
          setEditingBrand(null)
        }}
        brand={editingBrand}
      />
    </>
  )
}
BrandsTableInner.displayName = 'BrandsTableInner'

/** Brand management table with DataTable provider wrapper. */
export function BrandsTable({ tableKey }: BrandsTableProps) {
  return (
    <ColumnsStoreProvider tableKey={tableKey}>
      <DataTableProvider tableKey={tableKey}>
        <DataTableContainer>
          <BrandsTableInner tableKey={tableKey} />
        </DataTableContainer>
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
